import { useNavigationState } from "@/zustand/NavigationState";
import { usePaginatedMembersByChatId } from "@/middleware/hooks/usePaginatedMembersByChatId";
import ChatMember from "./ChatMember";
import { useEffect, useRef, useState } from "react";
import { toast } from "@/hooks/useToast";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Input } from "@/components/primitives/input";
import { SEARCH_DEBOUNCE_TIME } from "@/config/constants";
import { useDebounce } from "@uidotdev/usehooks";
import { usePaginatedMembersWithPrefixSearch } from "@/middleware/hooks/usePaginatedMembersWithPrefixSearch";
import { useTranslation } from "react-i18next";

export default function ChatMembersList() {
  const { t } = useTranslation();
  const activeChatId = useNavigationState((state) => state.activeChatId);

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, SEARCH_DEBOUNCE_TIME);

  const {
    status: defaultStatus,
    hasNextPage: defaultHasNextPage,
    isFetchingNextPage: defaultIsFetchingNextPage,
    data: defaultData,
    fetchNextPage: defaultFetchNextPage,
    error: defaultError,
  } = usePaginatedMembersByChatId({ chatId: activeChatId || "" });

  const {
    status: searchStatus,
    hasNextPage: searchHasNextPage,
    isFetchingNextPage: searchIsFetchingNextPage,
    // isFetching: searchIsFetching,
    data: searchData,
    fetchNextPage: searchFetchNextPage,
    error: searchError,
  } = usePaginatedMembersWithPrefixSearch({
    searchTerm: debouncedSearchTerm,
    member: activeChatId || "", // members inside this chat
  });

  // join all react query hook handlers
  const status = !debouncedSearchTerm ? defaultStatus : searchStatus;
  const hasNextPage = !debouncedSearchTerm
    ? defaultHasNextPage
    : searchHasNextPage;
  const isFetchingNextPage = !debouncedSearchTerm
    ? defaultIsFetchingNextPage
    : searchIsFetchingNextPage;
  const data = !debouncedSearchTerm ? defaultData : searchData;
  const fetchNextPage = !debouncedSearchTerm
    ? defaultFetchNextPage
    : searchFetchNextPage;
  const error = !debouncedSearchTerm ? defaultError : searchError;
  // // //

  const results = data ? data.pages.flat() : [];

  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasNextPage ? results.length + 1 : results.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 52,
    overscan: 5,
  });

  useEffect(() => {
    if (error) {
      toast({
        duration: 1500,
        description:
          error instanceof Error ? error.message : "Cannot load members",
        variant: "destructive",
      });
    }
  }, [error]);

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (
      lastItem.index >= results.length - 1 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasNextPage,
    fetchNextPage,
    results.length,
    isFetchingNextPage,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowVirtualizer.getVirtualItems(),
  ]);

  if (!activeChatId) {
    return null;
  }

  return (
    <>
      <div className="mt-2 flex px-4 py-1">
        <Input
          defaultValue={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value.toLocaleLowerCase())}
          type="text"
          placeholder={t("searchGroupMembers")}
          className="h-8 bg-foreground/5"
        />
      </div>
      {status === "pending" ? (
        <>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
            <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
          </div>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
            <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
          </div>
          <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
            <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
            <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
          </div>
        </>
      ) : (
        <div
          ref={parentRef}
          className="custom-scroll"
          style={{
            height: `100%`,
            width: `100%`,
            overflow: "auto",
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const isLoaderRow = virtualRow.index > results.length - 1;
              const member = results[virtualRow.index];

              return (
                <div
                  key={virtualRow.index}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {isLoaderRow ? (
                    hasNextPage && (
                      <>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                        <div className="flex items-center justify-start gap-3 truncate px-4 py-2 align-middle">
                          <div className="size-10 shrink-0 animate-pulse rounded-full bg-muted" />
                          <div className="h-8 w-full animate-pulse rounded-sm bg-muted" />
                          <div className="size-5 shrink-0 animate-pulse rounded-sm bg-muted" />
                        </div>
                      </>
                    )
                  ) : (
                    <ChatMember
                      key={member.username}
                      username={member.username}
                      role={member.role}
                      isAuth={member.isAuth}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
